import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useForm, useFormContext, FormProvider } from 'react-hook-form';
import { Modal, Image } from 'react-bootstrap';
import i18n from 'I18n';
import CloseIcon from '../../../assets/images/prompt_close_icon.svg';
import PromptButton, { BUTTON_TYPES } from 'components/base/prompt/BaseButtons';
import { PermissionCodes } from 'config/PermissionCodes';
import ContentSections from 'components/base/ContentSections';
import Loading from 'components/base/Loading';
import AuthButton from 'components/base/AuthButton';
import deleteImage from '../../../assets/images/deleteIcon.png';
import { CustomInput } from 'components/base/CustomTitleWithInput';
import ErrorFieldMessage, { hasError, ReactHookFormErrorMessage } from 'components/base/ErrorFieldMessage';
import { LevelErrorHandleFields } from './LevelErrorHandle';
import { ReactHookFormMarkDown } from 'components/base/CustomMarkDownArea';
import { LanguageConfig } from 'config/CustomEnums';
import './LevelRulesModal.scss';

const updateLevelRulesValidate = ({
  getValues,
  setError,
  clearErrors
}) => {
  const [levelRules] = getValues(["levelRules"]);

  const length = levelRules?.length;

  if (!length) {
    return true;
  }

  let isValid = true;

  for (let index = 0; index < length; index++) {
    const titleField = `levelRules.[${index}].translations.en.title`;
    const contentField = `levelRules.[${index}].translations.en.content`;

    clearErrors([
      contentField
    ]);

    const [
      title,
      content,
    ] = getValues([
      titleField,
      contentField
    ]);

    if (!title || !content) {
      isValid = false;
      setError(
        contentField,
        {
          type: 'require',
          message: LevelErrorHandleFields.titleAndContent,
        },
      );
    }
  }

  return isValid;
};



const LevelRuleSection = ({
  language,
}) => {
  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const watchLevelRules = watch('levelRules') || [{}];

  const rewardItem = (item, index) => {
    const titleField = `levelRules.[${index}].translations.${language}.title`;
    const contentField = `levelRules.[${index}].translations.${language}.content`;

    const watchTitle = watch(titleField);
    const watchContent = watch(contentField);

    return (
      <div>
        <div className={`level-rules-section-title-container level-rules-section-title-container-${index}`}>
          <label className='create-section-label level-rules-section-title'>
            {`# ${index + 1}`}
          </label>
          <AuthButton
            children={
              <Image src={deleteImage} className="custom-delete-btn" />
            }
            customClass="custom-delete-btn"
            action={() => {
              // eslint-disable-next-line array-callback-return
              const newList = watchLevelRules?.filter((item1, index1) => {
                // eslint-disable-next-line eqeqeq
                if (index != index1) {
                  return item1;
                }
              });
              setValue('levelRules', newList, { shouldDirty: true });
            }}
          />
        </div>
        <div className='level-rules-input-container'>
          <CustomInput
            onChange={({ target }) => {
              setValue(titleField, target.value, { shouldDirty: true, });
            }}
            value={watchTitle}
            inputCustomClass={"level-rules-title"}
            error={hasError(errors, contentField)}
          />
        </div>

        <ReactHookFormMarkDown
          content={{
            label: null,
            error:
              language === LanguageConfig.english
                ? hasError(errors, contentField)
                : false,
            key: contentField,
            focus: () => { },
          }}
        />
        <ReactHookFormErrorMessage
          errors={errors}
          id={contentField}
        />
      </div>
    )
  }

  return (
    <>
      {watchLevelRules?.map((item, index) => rewardItem(item, index))}
      <AuthButton
        title={i18n.t("addANewSection", { locale: language })}
        customClass="btn-back-button-common level-rules-add-button"
        action={() => {
          setValue('levelRules', [...watchLevelRules, {}]);
        }}
      />
    </>
  );
}


const LevelRulesModal = ({
  languages,
  levelRulesLoading,
  levelRulesList,
  show,
  setShow,
}) => {
  const dispatch = useDispatch();

  const methods = useForm({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      levelRules: levelRulesList,
    },
  });

  const { getValues, setError, clearErrors } = methods;

  useEffect(() => {
    dispatch({
      type: 'language/getList',
      payload: { isSelectorLoad: true }
    });
  }, [dispatch])

  useEffect(() => {
    if (show) {
      dispatch({
        type: 'levels/getLevelRules',
        payload: {},
      });
    }
  }, [dispatch, show]);

  useEffect(() => {
    methods.reset({ levelRules: levelRulesList });
  }, [dispatch, methods, levelRulesList])

  const onClose = () => {
    setShow(false);
    dispatch({
      type: 'levels/updateState',
      payload: {
        levelRulesLoading: false,
        levelRulesList: [],
      },
    })
  }

  const saveAction = () => {
    const isValid = updateLevelRulesValidate({
      getValues,
      setError,
      clearErrors
    })

    if (!isValid) {
      return;
    };

    dispatch({
      type: 'levels/updateLevelRules',
      payload: {
        data: getValues(),
        afterAction: onClose,
      },
    })
  };

  const getTabs = (language) => {
    if (levelRulesLoading) {
      return [
        <Loading />,
      ]
    }
    return [
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit((data) => {
            console.log(data);
          })}
        >
          <LevelRuleSection
            language={language}
          />
        </form>
      </FormProvider>
    ];
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop='static'
    >
      <Modal.Body className={`base-prompt-container`}>
        <Image
          src={CloseIcon}
          className="base-prompt-close-button"
          onClick={onClose}
        />
        <div className="first-section-title">Level Rules</div>

        <div className='level-rules-container'>
          <ContentSections
            contentStyle='level-rules-content-sections'
            languageTabContent={{
              containers: languages?.map((item) => ({
                container: getTabs(item.code),
                key: item.code,
                title: item.sourceName,
              })),
            }}
            hidePreview
          />
        </div>

        <div className="base-prompt-buttons">
          <PromptButton
            title='Cancel'
            action={onClose}
            type={BUTTON_TYPES.secondary}
          />
          <PromptButton
            title='Confirm'
            action={saveAction}
            requires={PermissionCodes.changeLevel}
            type={BUTTON_TYPES.main}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
}

const mapPropsToState = (state) => ({
  languages: state.language.allList,
  levelRulesLoading: state.levels.levelRulesLoading,
  levelRulesList: state.levels.levelRulesList,
});

export default connect(mapPropsToState)(LevelRulesModal);
